import { HEDERA_MIRRRORNODE, HEDERA_MIRRRORNODE_TESTNET, HEDERA_MAINNET } from '@/config/constants'

const CACHE_TTL_MS = 60_000

type CacheEntry = {
  data: unknown
  timestamp: number
}

// Module-level singleton: survives re-renders, resets on page reload
const cache = new Map<string, CacheEntry>()

const getCacheKey = (chainId: string, path: string): string => `${chainId}:${path}`

const getBaseUrl = (chainId: string): string =>
  chainId === HEDERA_MAINNET ? HEDERA_MIRRRORNODE : HEDERA_MIRRRORNODE_TESTNET

/**
 * Fetches a Hedera Mirror Node endpoint with a 1-minute in-memory cache segmented by chainId.
 * Returns null for 404 responses (resource not found is a valid, cacheable result).
 * Throws for other non-OK responses.
 */
export const mirrorNodeFetch = async <T>(path: string, chainId: string): Promise<T | null> => {
  const key = getCacheKey(chainId, path)
  const cached = cache.get(key)

  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.data as T | null
  }

  const url = `${getBaseUrl(chainId)}${path}`
  const response = await fetch(url)

  if (response.status === 404) {
    cache.set(key, { data: null, timestamp: Date.now() })
    return null
  }

  if (!response.ok) {
    throw new Error(`Mirror Node fetch failed [${response.status} ${response.statusText}]: ${path}`)
  }

  const data = (await response.json()) as T
  cache.set(key, { data, timestamp: Date.now() })
  return data
}
